<template>
  <EXTooltip title="Click to see your badges" placement="top">
    <div
      class="badges-dashboard-card badges-dashboard-card--clickable"
      role="link"
      tabindex="0"
      @click="onNavigate"
      @keydown.enter.prevent="onNavigate"
      @keydown.space.prevent="onNavigate"
    >
      <div class="badges-dashboard-card__header">
        <div class="badges-dashboard-card__icon-container">
          <Icon name="badge_icon" class="badges-dashboard-card__icon"/>
        </div>
        <div class="badges-dashboard-card__text-container">
          <div class="badges-dashboard-card__title">
            {{ cardTitle }} ({{ total_badges }})
          </div>
          <div class="badges-dashboard-card__subtitle">
            Count by Readiness Level
          </div>
        </div>
      </div>
      <div class="badges-dashboard-card__body">
        <EmployeeBadgeCounter
          :count_of_apprentice="apprentice_badge_count"
          :count_of_proficient="professional_badge_count"
          :count_of_coach="coach_badge_count"
          :count_of_architect="master_badge_count"
        />
      </div>
    </div>
  </EXTooltip>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import { useRouter } from "vue-router/composables";
import Icon from "@components/icons/Icon.vue";
import EmployeeBadgeCounter from "@components/EmployeeBadgeCounter/EmployeeBadgeCounter.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";

const emit = defineEmits(["navigateToEmployeeBadges"]);

const instance = getCurrentInstance();
const router = instance?.proxy?.$router || useRouter();

const props = defineProps({
  apprentice_badge_count: {
    type: Number,
    default: 0,
  },
  professional_badge_count: {
    type: Number,
    default: 0,
  },
  coach_badge_count: {
    type: Number,
    default: 0,
  },
  master_badge_count: {
    type: Number,
    default: 0,
  },
  cardTitle: {
    type: String,
    default: "Your Badges",
  },
});

const total_badges = computed(() => {
  return (
    (props.apprentice_badge_count || 0) +
    (props.professional_badge_count || 0) +
    (props.coach_badge_count || 0) +
    (props.master_badge_count || 0)
  );
});

function onNavigate() {
  if (router && typeof router.push === "function") {
    router
      .push({ name: "YourStuffEmployeeBadges" })
      .catch(() => {});
    return;
  }
  emit("navigateToEmployeeBadges");
}
</script>

<style scoped>
.badges-dashboard-card {
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #EAECF0;
  background: #FFF;
  box-shadow: 0px 1px 2px 0px #1018280D;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 330px;
  max-width: 330px;
  height: 162px;
}

.badges-dashboard-card--clickable {
  cursor: pointer;
}

.badges-dashboard-card--clickable:focus-visible {
  outline: 2px solid #667085;
  outline-offset: 2px;
}

.badges-dashboard-card__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.badges-dashboard-card__icon-container {
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  border: 1px solid #EAECF0;
  background: #FFF;
}

::v-deep .badges-dashboard-card__icon path {
  stroke: #667085;
}

::v-deep .badges-dashboard-card__icon circle {
  stroke: #667085;
}

.badges-dashboard-card__text-container {
  display: flex;
  flex-direction: column;
  position: relative;
  top: 1px;
}

.badges-dashboard-card__title {
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
}

.badges-dashboard-card__subtitle {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.badges-dashboard-card__body {
  display: flex;
  justify-content: end;
  position: relative;
  left: -10px;
  top: -6px;
}
</style>
